import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  TableSortLabel,
  IconButton,
  Typography,
} from "@mui/material";
import "./CommonTable2.scss";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

export interface Column {
  field: string;
  headerName: string;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  fontWeight?: number;
  statusOpen?: boolean;
}

export interface CommonTableProps {
  columns: Column[];
  rows: Record<string, unknown>[];
  onRowClick?: (row: Record<string, unknown>) => void;
  loading?: boolean;
  statusOpen?: boolean | ((row: Record<string, unknown>) => boolean);
}

type SortOrder = "asc" | "desc";
type StatusSortMode = "approved" | "waiting" | "rejected" | null;

const CommonTable2: React.FC<CommonTableProps> = ({
  columns,
  rows,
  onRowClick,
  statusOpen,
  loading = false,
}) => {
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [order, setOrder] = useState<SortOrder>("asc");
  const [statusSortMode, setStatusSortMode] = useState<StatusSortMode>(null);

  const hasAction = columns.some((col) => col.field === "action");

  const statusPriority: Record<string, number> = {
    waiting: 2,
    pending: 2,
    approved: 1,
    rejected: 3,
  };

  const getStatusPriority = (status: string, mode: StatusSortMode): number => {
    const lowerStatus = status.toLowerCase();
    let baseStatus: string;

    if (lowerStatus.includes("pending") || lowerStatus.includes("waiting")) {
      baseStatus = "waiting";
    } else if (lowerStatus.includes("approved")) {
      baseStatus = "approved";
    } else if (lowerStatus.includes("rejected")) {
      baseStatus = "rejected";
    } else {
      return 999;
    }

    // Adjust priority based on current sort mode
    if (mode === "approved") {
      // approved: 1, waiting: 2, rejected: 3
      return statusPriority[baseStatus];
    } else if (mode === "waiting") {
      // waiting: 1, approved: 2, rejected: 3
      if (baseStatus === "waiting") return 1;
      if (baseStatus === "approved") return 2;
      if (baseStatus === "rejected") return 3;
    } else if (mode === "rejected") {
      // rejected: 1, waiting: 2, approved: 3
      if (baseStatus === "rejected") return 1;
      if (baseStatus === "waiting") return 2;
      if (baseStatus === "approved") return 3;
    }

    return statusPriority[baseStatus];
  };

  const handleSort = (field: string) => {
    if (field === "status") {
      // Cycle through: approved -> waiting -> rejected -> null (back to approved)
      if (statusSortMode === null || statusSortMode === "rejected") {
        setStatusSortMode("approved");
      } else if (statusSortMode === "approved") {
        setStatusSortMode("waiting");
      } else if (statusSortMode === "waiting") {
        setStatusSortMode("rejected");
      }
      setOrderBy(field);
      setOrder("asc");
    } else {
      // Normal sorting for other columns
      const isAsc = orderBy === field && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(field);
      setStatusSortMode(null); // Reset status sort mode when sorting other columns
    }
  };

  const sortedRows = useMemo(() => {
    if (!orderBy) return rows;

    return [...rows].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      // Special handling for status column
      if (orderBy === "status") {
        const aPriority = getStatusPriority(String(aValue), statusSortMode);
        const bPriority = getStatusPriority(String(bValue), statusSortMode);
        return aPriority - bPriority;
      }

      // Generic sorting for other columns
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (order === "asc") {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }, [rows, orderBy, order, statusSortMode]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page]);

  return (
    <>
      <Paper
        elevation={1}
        sx={{ border: "1px solid #e0e0e0", borderRadius: "0px", width: "100%" }}
      >
        <TableContainer component={Paper} className="common-table2">
          <Table>
            {/* Table Header */}
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align || "center"}
                    sx={{ textAlign: col.align || "center" }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.field}
                        direction={orderBy === col.field ? order : "asc"}
                        onClick={() => handleSort(col.field)}
                        hideSortIcon={false}
                        sx={{
                          "&.MuiTableSortLabel-root": {
                            color: "#ffffff",
                            "& .MuiTableSortLabel-icon": {
                              opacity: orderBy === col.field ? 1 : 1,
                              color: "#ffffff",
                            },
                          },
                          "&:hover .MuiTableSortLabel-icon": {
                            opacity: 1,
                          },
                        }}
                      >
                        {col.headerName}
                      </TableSortLabel>
                    ) : (
                      col.headerName
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (hasAction ? 1 : 0)}
                    align="center"
                  >
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((row, i) => (
                  <TableRow
                    key={i}
                    hover
                    onClick={() => onRowClick && onRowClick(row)}
                    sx={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {columns.map((col) => {
                      // ✅ Handle "status" column styling (your existing code)
                      if (col.field === "status") {
                        const status = String(row[col.field]);
                        return (
                          <TableCell
                            key={col.field}
                            align={col.align || "center"}
                            sx={{ fontWeight: col.fontWeight ?? 500 }} // 👈 only body cells
                          >
                            <span
                              className={`status ${
                                status.includes("Waiting")
                                  ? "waiting"
                                  : status.includes("Pending")
                                  ? "pending"
                                  : status.includes("Approved")
                                  ? "approved"
                                  : "rejected"
                              }`}
                            >
                              {status}
                            </span>
                          </TableCell>
                        );
                      }
                      if (
                        col.field === "print_request_status" ||
                        col.field === "print_request"
                      ) {
                        const status = String(row[col.field] || "");

                        let className = "status-box";

                        if (status === "not_yet_requested")
                          className += " status-notyet";
                        else if (status === "requested")
                          className += " status-requested";
                        else if (status === "approved")
                          className += " status-approved";
                        else if (status === "printed")
                          className += " status-printed";
                        else if (status === "rejected")
                          className += " status-rejected";

                        return (
                          <TableCell
                            key={col.field}
                            align={col.align || "center"}
                            sx={{ fontWeight: col.fontWeight ?? 500 }}
                          >
                            <span className={className}>
                              {(typeof statusOpen === "boolean"
                                ? statusOpen
                                : statusOpen?.(row)) && (
                                <OpenInNewIcon className="status-arrow" />
                              )}
                            </span>
                          </TableCell>
                        );
                      }

                      // ✅ Handle "year_levels" custom rendering
                      if (col.field === "year_levels") {
                        const yearLevels = Array.isArray(row[col.field])
                          ? (row[col.field] as any[])
                          : [];
                        return (
                          <TableCell
                            key={col.field}
                            align={col.align || "center"}
                            sx={{ fontWeight: col.fontWeight ?? 500 }} // 👈 only body cells
                          >
                            {yearLevels.length > 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                {yearLevels.map((item, index) => (
                                  <span
                                    key={index}
                                    style={{
                                      fontSize: "13px",
                                      color: "#333",
                                      background: "#91e2ed",
                                      padding: "2px 6px",
                                      borderRadius: "6px",
                                      display: "inline-block",
                                      width: "fit-content",
                                      margin: "0 auto",
                                    }}
                                  >
                                    {typeof item === "string"
                                      ? item
                                      : item?.year_group?.year_group ||
                                        item?.year_group ||
                                        "-"}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        );
                      }

                      // ✅ Default rendering for other fields
                      return (
                        <TableCell
                          key={col.field}
                          align={col.align || "center"}
                          sx={{ fontWeight: col.fontWeight ?? 500 }} // 👈 only body cells
                        >
                          {row[col.field] as React.ReactNode}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (hasAction ? 1 : 0)}
                    className="no-records"
                    align="center"
                  >
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > 10 && (
          <Box display="flex" alignItems="center" p={2} gap={"2px"}>
            <Box>
              <IconButton
                onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                disabled={page === 0}
                size="small"
              >
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={() =>
                  setPage((prev) =>
                    Math.min(prev + 1, Math.ceil(rows.length / rowsPerPage) - 1)
                  )
                }
                disabled={page >= Math.ceil(rows.length / rowsPerPage) - 1}
                size="small"
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography sx={{ fontSize: "12px", fontWeight: "600" }}>
              Page {page + 1} of {Math.ceil(rows.length / rowsPerPage)}
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

export default CommonTable2;
